import type { CountryName } from '@/helpers/country'
import { getCountryCodeFromName } from '@/helpers/country'
import type { PhoneNumber } from 'libphonenumber-js'
import parsePhoneNumber from 'libphonenumber-js'
import { z } from 'zod'

const e164PhoneNumber = (countryName: CountryName) => {
  const countryCode = getCountryCodeFromName(countryName) || 'US' // Default to US if country is not found
  let phoneNumberObj: PhoneNumber | undefined = undefined

  return z
    .string()
    .refine(
      value => {
        phoneNumberObj = parsePhoneNumber(value, {
          defaultCountry: countryCode,
          extract: true, // less strict parsing
        })
        return phoneNumberObj?.isValid()
      },
      {
        message: 'Invalid phone number format. Expected E.164 format.',
      },
    )
    .transform(() => phoneNumberObj?.format('E.164'))
}

/**
 * returns a Zod schema that validates phone numbers in E.164 format
 * for the specified country.
 * @param value - The phone number to validate.
 * @param countryName - The name of the country to validate against.
 * @returns A Zod validation result with the phone number in E.164 format if successful, otherwise an error message.
 * @example
 * validatePhoneNumber('+1234567890', 'United States of America')
 * // returns { success: true, data: '+1234567890' }
 */
export const validatePhoneNumber = (
  value: string,
  countryName: CountryName,
) => {
  return e164PhoneNumber(countryName).safeParse(value)
}

/**
 * Validates a SIP address.
 * The SIP address must start with "sip:" or "sips:", followed by a user part and a domain.
 * Optionally, it can include a port, parameters, and headers.
 * @example
 * validateSipAddress('sip:user@host[:port][;parameters][?headers]')
 * // returns { success: true, data: 'sip:user@host[:port]' }
 */
const sipAddress = z.string().refine(
  value => {
    // SIP URI format: sip:user@host[:port][;parameters][?headers]
    // or sips:user@host[:port][;parameters][?headers]
    const sipRegex =
      /^sips?:([a-zA-Z0-9._-]+)@([a-zA-Z0-9.-]+|\[[0-9a-fA-F:]+\])(?::(\d+))?(?:;[^?]*)?(?:\?.*)?$/

    if (!sipRegex.test(value)) {
      return false
    }

    const match = sipRegex.exec(value)
    if (!match) return false

    const [, user, host, port] = match

    // Validate user part (must not be empty)
    if (!user || user.length === 0) {
      return false
    }

    // Validate host part (must not be empty)
    if (!host || host.length === 0) {
      return false
    }

    // Validate port if present
    if (port) {
      const portNum = parseInt(port, 10)
      if (portNum < 1 || portNum > 65535) {
        return false
      }
    }

    return true
  },
  {
    message:
      'Invalid SIP address format. Expected format: sip:user@host[:port] or sips:user@host[:port]',
  },
)

/**
 *  Validates a SIP address.
 * The SIP address must start with "sip:" or "sips:", followed by a user part and a domain.
 * @param value - The SIP address to validate.
 * @returns  A Zod validation result.
 */
export const validateSipAddress = (value: string) => {
  return sipAddress.safeParse(value)
}
